/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { formatUserDateTimeWithPreferences } from '../../utils/commonColumns'
import HtmlCell from '../shared/HtmlCell'
import { WorkflowCollaboratorsModal } from '../modals/WorkflowCollaboratorsModal'
import { WorkflowEditModal } from '../modals/WorkflowEditModal'
import { WorkflowReviewModal } from '../modals/WorkflowReviewModal'
import { WorkflowApproversModal } from '../modals/WorkflowApproversModal'
import { WorkflowQcApproversModal } from '../modals/WorkflowQcApproversModal'

interface WorkflowStepsProps {
  workflow: any
  /** Callback when workflow is updated (e.g., after adding collaborators) */
  onWorkflowUpdate?: () => void
}

const WF_TYPES = {
  obsolete: 'obsolete',
  referencePlaceholder: 'referencePlaceholder',
  training: 'training',
  eos: 'eos',
  qcReview: 'qcReview',
  standard: 'standard'
}

/**
 * WorkflowSteps Component
 *
 * Displays workflow steps with progress visualization.
 * Matches the legacy Steps.jsx component behavior exactly.
 *
 * Key behaviors:
 * - Filters steps based on stepsWithContent.noApprovers flag
 * - Shows "Read & Acknowledge" steps regardless of approvers
 * - Applies dynamic CSS classes based on workflow type and step count
 * - Displays step details including task team members with email links
 */
export const WorkflowSteps: React.FC<WorkflowStepsProps> = ({ workflow, onWorkflowUpdate }) => {
  const { t } = useTranslation()
  const { service } = useServiceContext()
  const { showModal } = useModal()
  const [wfType, setWfType] = useState(WF_TYPES.standard)
  const [filteredWorkflowSteps, setFilteredWorkflowSteps] = useState<any[]>([])
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})

  // Get user date/time preferences
  const user = service.auth.getUser()
  const userPreferences = {
    dateFormat: user?.dateFormat,
    timeFormat: user?.timeFormat,
    timeFormat24h: user?.timeFormat24h
  }

  const determineWorkflowType = (workflowType: string): string => {
    switch (workflowType) {
      case 'OBSOLETE':
      case 'OBSOLETE_REFERENCE':
      case 'OBSOLETE_PLACEHOLDER':
        return WF_TYPES.obsolete
      case 'CREATE_PLACEHOLDER':
      case 'CREATE_REFERENCE':
        return WF_TYPES.referencePlaceholder
      case 'TRAINING':
        return WF_TYPES.training
      case 'EOS_DOWNLOAD_AND_ACKNOWLEDGE':
        return WF_TYPES.eos
      case 'QC_REVIEW':
        return WF_TYPES.qcReview
      default:
        return WF_TYPES.standard
    }
  }

  const shouldIncludeStep = (stepIndex: number, workflow: any): boolean => {
    const stepHasApprovers = workflow.stepsWithContent?.[stepIndex]?.noApprovers === false

    const isReadAndAcknowledge = workflow.workflowSteps?.[stepIndex]?.readableType === 'Read & Acknowledge'

    return stepHasApprovers || isReadAndAcknowledge
  }

  const filterWorkflowSteps = (workflow: any): any[] => {
    const result: any[] = []

    if (!workflow?.workflowSteps) {
      return result
    }

    // If stepsWithContent doesn't exist, show all steps (don't filter)
    if (!workflow.stepsWithContent) {
      return workflow.workflowSteps.filter((step: any) => step != null)
    }

    // Use the filtering logic from legacy when stepsWithContent exists
    for (let counter = 0; counter < workflow.workflowSteps.length; counter++) {
      if (workflow.workflowSteps[counter] && shouldIncludeStep(counter, workflow)) {
        result.push(workflow.workflowSteps[counter])
      }
    }

    return result
  }

  useEffect(() => {
    setWfType(determineWorkflowType(workflow.workflowType))
    setFilteredWorkflowSteps(filterWorkflowSteps(workflow))
  }, [workflow])

  const getClassNameBasedOnStep = () => {
    switch (filteredWorkflowSteps?.length) {
      case 1:
        return ' one'
      case 2:
        return ' two'
      case 3:
        return ' three'
      case 4:
        return ' four'
      default:
        return ' '
    }
  }

  const isCancelled = workflow.status === 'CANCELLED' || workflow.status === 'SUSPENDED' || workflow.isRejected

  /**
   * Opens the appropriate workflow editor modal based on step type and workflow state
   */
  const openEditorModal = useCallback(
    (step: any, teamStep: any) => {
      // Check if we should show "Add Collaborators" modal (for active step with insufficient approvers)
      const shouldShowAddCollaborators = step?.status === 'ACTIVE' && teamStep?.countOfDone < teamStep?.nofAllValue

      if (shouldShowAddCollaborators) {
        showModal(
          <WorkflowCollaboratorsModal workflowId={workflow.id} workflow={workflow} onSuccess={onWorkflowUpdate} />
        )
      } else if (step?.type === 'REVISE_EDIT') {
        showModal(<WorkflowEditModal workflowId={workflow.id} workflow={workflow} onSuccess={onWorkflowUpdate} />)
      } else if (step?.type === 'REVIEW') {
        showModal(<WorkflowReviewModal workflowId={workflow.id} workflow={workflow} onSuccess={onWorkflowUpdate} />)
      } else if (step?.type === 'APPROVE') {
        showModal(<WorkflowApproversModal workflowId={workflow.id} workflow={workflow} onSuccess={onWorkflowUpdate} />)
      } else if (step?.type === 'APPROVE_QC') {
        showModal(
          <WorkflowQcApproversModal workflowId={workflow.id} workflow={workflow} onSuccess={onWorkflowUpdate} />
        )
      }
    },
    [workflow, showModal, onWorkflowUpdate]
  )

  /**
   * Renders the insufficient approvers warning for a step
   * Shows appropriate warning/error message with clickable link to open editor modal
   */
  const renderInsufficientApproversWarning = (step: any, teamStep: any) => {
    if (!teamStep?.insufficientNOOfApprovers) {
      return null
    }

    const readableType = step?.readableType
    const isAuthorization = readableType === 'Authorization'
    const isActiveStep = step?.status === 'ACTIVE'
    const cssClass = isAuthorization ? 'dotted-error' : 'dotted-warning'

    const handleClick = (e: React.MouseEvent) => {
      // Only handle clicks on anchor tags
      if ((e.target as HTMLElement).tagName === 'A') {
        e.preventDefault()
        e.stopPropagation()
        openEditorModal(step, teamStep)
      }
    }

    // Authorization phase - single error message
    if (isAuthorization) {
      return (
        <div className={cssClass} onClick={handleClick}>
          <HtmlCell>{t('WORKFLOW_INSUFFICIENT_NO_OF_USERS_LONG')}</HtmlCell>
        </div>
      )
    }

    // Other phases (Edit, Review, Approval, QC Review, QC Approval)
    const showPhaseMessages = ['QC Review', 'QC Approval', 'Edit', 'Review', 'Approval'].includes(readableType)
    if (!showPhaseMessages) {
      return null
    }

    return (
      <div className={cssClass} onClick={handleClick}>
        <HtmlCell>{t('WORKFLOW_INSUFFICIENT_NO_OF_USERS')}</HtmlCell>
        {isActiveStep && <HtmlCell>{t('WORKFLOW_INSUFFICIENT_NO_OF_USERS_LONG2')}</HtmlCell>}
        {workflow?.canEditWorkflowCollaborators && !teamStep?.activeStep && readableType === 'Edit' && (
          <HtmlCell>{t('WORKFLOW_INSUFFICIENT_NO_OF_USERS_EDITPHASE_LONG')}</HtmlCell>
        )}
        {workflow?.canEditWorkflowReviewers && !teamStep?.activeStep && readableType === 'Review' && (
          <HtmlCell>{t('WORKFLOW_INSUFFICIENT_NO_OF_USERS_REVIEWPHASE_LONG')}</HtmlCell>
        )}
        {workflow?.canEditWorkflowApprovers &&
          !teamStep?.activeStep &&
          (readableType === 'Approval' || readableType === 'QC Approval') && (
            <HtmlCell>
              {t(
                readableType === 'QC Approval'
                  ? 'WORKFLOW_INSUFFICIENT_NO_OF_USERS_QC_APPROVEPHASE_LONG'
                  : 'WORKFLOW_INSUFFICIENT_NO_OF_USERS_APPROVEPHASE_LONG'
              )}
            </HtmlCell>
          )}
      </div>
    )
  }

  const toggleItemExpansion = (itemKey: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }))
  }

  const formatDateTime = (timestamp: number | null): string => {
    if (!timestamp) return ''
    return formatUserDateTimeWithPreferences(timestamp, true, userPreferences)
  }

  const getTaskDetailMessage = (item: any, step: any, teamStep: any): string => {
    const isEosWf = workflow.workflowType === 'EOS_DOWNLOAD_AND_ACKNOWLEDGE'
    const stepType = step?.readableType || ''
    const rejectionReview = teamStep?.rejectionReview || false

    let messageKey = isEosWf ? 'WORKFLOW_EOS_TASK_TYPE_DETAILS' : 'WORKFLOW_TASK_TYPE_DETAILS'

    const taskStartDate = formatDateTime(item.startDate)
    const params: any = {
      taskStartDate: taskStartDate,
      taskType: rejectionReview ? t('REJECTION_REVIEW') : stepType
    }

    if (item.endDate) {
      messageKey = isEosWf ? 'WORKFLOW_EOS_TASK_TYPE_DETAILS_AND_STATUS' : 'WORKFLOW_TASK_TYPE_DETAILS_AND_STATUS'
      params.taskEndDate = formatDateTime(item.endDate)

      switch (item.status) {
        case 'REJECTED':
          params.taskResult = t('WORKFLOW_TASK_STEP_ASSIGNED_TASK_REJECTED')
          break
        case 'APPROVED':
          params.taskResult = t('WORKFLOW_TASK_STEP_ASSIGNED_TASK_COMPLETED')
          break
        case 'CANCELLED':
          params.taskResult = t('WORKFLOW_TASK_STEP_ASSIGNED_TASK_CANCELED')
          break
        case 'SUSPENDED':
          params.taskResult = t('WORKFLOW_TASK_STEP_ASSIGNED_TASK_SUSPENDED')
          break
      }
    }

    // Disable HTML escaping for date interpolation since dates contain forward slashes
    // that i18next escapes as &#x2F; by default for XSS protection
    const result = t(messageKey, { ...params, interpolation: { escapeValue: false } })
    return typeof result === 'string' ? result : String(result)
  }

  const renderTaskTeamItem = (item: any, itemIndex: number, step: any, teamStep: any, teamStepIndex: number) => {
    const itemKey = `${step.id}-${teamStep.id || teamStepIndex}-${item.id || item.userId || itemIndex}`
    const isExpanded = expandedItems[itemKey] || false
    const isDone = item.status !== 'OPEN' && item.status !== 'INACTIVE'
    const isCancelled = item.status === 'CANCELLED'

    return (
      <div key={itemKey} className={`dotted${isDone ? ' done' : ''}${isCancelled ? ' open' : ''}`}>
        {/* Plus/Minus toggle button */}
        {!isExpanded && (
          <i
            className="fa fa-plus cursor-pointer"
            aria-hidden="true"
            onClick={() => toggleItemExpansion(itemKey)}
            style={{ marginRight: '5px' }}
          ></i>
        )}
        {isExpanded && (
          <i
            className="fa fa-minus cursor-pointer"
            aria-hidden="true"
            onClick={() => toggleItemExpansion(itemKey)}
            style={{ marginRight: '5px' }}
          ></i>
        )}

        {/* User name with email link */}
        <a href={`mailto:${item.userEmail}`}>{item.userName}</a>

        {/* Expanded task details */}
        {isExpanded && item.status !== 'INACTIVE' && (
          <ul className={`collaborators${isDone ? ' done' : ''}`}>
            <li>{getTaskDetailMessage(item, step, teamStep)}</li>
          </ul>
        )}
      </div>
    )
  }

  // Build dynamic className for steps header
  const stepsHeaderClassName =
    'steps' +
    ([WF_TYPES.eos].includes(wfType) ? ' two' : '') +
    ([WF_TYPES.obsolete, WF_TYPES.referencePlaceholder, WF_TYPES.standard, WF_TYPES.qcReview].includes(wfType) &&
    filteredWorkflowSteps?.length
      ? getClassNameBasedOnStep()
      : '') +
    (wfType === WF_TYPES.training ? ' one' : '') +
    (isCancelled ? ' canceled' : '')

  // Build dynamic className for steps content
  const stepsContentClassName =
    'steps-content' +
    ([WF_TYPES.obsolete, WF_TYPES.referencePlaceholder, WF_TYPES.eos].includes(wfType) ? ' two' : '') +
    (wfType === WF_TYPES.training ? ' one' : '') +
    ([WF_TYPES.standard, WF_TYPES.qcReview].includes(wfType) && filteredWorkflowSteps?.length
      ? getClassNameBasedOnStep()
      : '') +
    (isCancelled ? ' canceled' : '')

  if (!workflow?.workflowSteps || filteredWorkflowSteps.length === 0) {
    return null
  }

  return (
    <>
      {/* Steps Header - Progress indicators */}
      <div className={stepsHeaderClassName}>
        {filteredWorkflowSteps.map((step: any, index: number) => (
          <div key={step?.id || `step-${step?.type}-${index}`} className={step?.status === 'DONE' ? 'done' : ''}>
            <div className="step-header" style={{ fontSize: '14px', fontWeight: 'bold' }}>
              <span>{index + 1}</span> {step?.readableType}
            </div>
            <div className="flow-progress"></div>
          </div>
        ))}
      </div>

      {/* Steps Content - Detailed step information */}
      <div className={stepsContentClassName}>
        {filteredWorkflowSteps.map((step: any, index: number) => (
          <div
            key={step?.id || `step-content-${step?.type}-${index}`}
            className={`first${step.status === 'DONE' ? ' done' : ''}`}
          >
            <div className="flow-content">
              &nbsp;
              {/* Read & Acknowledge special messages */}
              {step?.readableType === 'Read & Acknowledge' && workflow?.stepsWithContent?.[4]?.notRequired && (
                <div className="dotted">{t('WORKFLOW_RA_NOT_REQUIRED')}</div>
              )}
              {step?.readableType === 'Read & Acknowledge' && workflow?.stepsWithContent?.[4]?.noRA && (
                <div className="dotted">{t('WORKFLOW_DETAIL_NOT_RA_FOR_THIS_CR')}</div>
              )}
              {step?.readableType === 'Read & Acknowledge' && workflow?.stepsWithContent?.[4]?.effectiveDate && (
                <div className="dotted">
                  {t('EFFECTIVE_DATE')}:{' '}
                  {formatUserDateTimeWithPreferences(
                    workflow.stepsWithContent[4].effectiveDate,
                    false,
                    userPreferences
                  )}
                </div>
              )}
              {/* Task Team Steps */}
              {step?.taskTeamSteps?.map((teamStep: any, teamStepIndex: number) => (
                <div key={teamStep?.id || `team-step-${teamStepIndex}`}>
                  {/* Step Order Info - "Step 1" and "Order: ..." */}
                  {!teamStep.rejectionReview &&
                    (teamStep.taskTeamItems?.length > 0 || teamStep.processingType === 'N_OF_ALL') &&
                    determineWorkflowType(workflow.workflowType) !== WF_TYPES.eos && (
                      <>
                        <strong className="collaborators step">
                          {t('STEP')} {teamStepIndex + 1}
                          {teamStep.taskTeamStepLabel?.text ? ' - ' + teamStep.taskTeamStepLabel.text : ''}
                        </strong>
                        <div className="order-info">
                          {t('ORDER')}: {t('WF_PROCESSING_TYPE_' + teamStep.processingType)}
                          {teamStep.processingType === 'N_OF_ALL' && (
                            <span>
                              ,{' '}
                              {t('WORKFLOW_STEPS_OF', {
                                done: teamStep.countOfDone,
                                total: teamStep.nofAllValue
                              })}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                  {/* Insufficient Approvers Warning */}
                  {renderInsufficientApproversWarning(step, teamStep)}

                  {/* Rejection Review */}
                  {teamStep.rejectionReview &&
                    (teamStep.taskTeamItems?.length > 0 || teamStep.processingType === 'N_OF_ALL') && (
                      <strong className="collaborators step">{t('REJECTION_REVIEW')}</strong>
                    )}

                  {/* Task Team Items - User list with email links */}
                  {teamStep.taskTeamItems
                    ?.filter((item: any) => !(item.startDate == null && item.status === 'CANCELLED'))
                    .map((item: any, itemIndex: number) =>
                      renderTaskTeamItem(item, itemIndex, step, teamStep, teamStepIndex)
                    )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export default WorkflowSteps
